import { StyleDeclarationValue } from 'aphrodite';
import { useState } from 'react';
import {
	Dropdown,
	DropdownCheckboxItem,
	DropdownContent,
	DropdownSelectTrigger,
	DropdownSeparator,
} from '../../components-v2/Dropdown';
import { styleSheet } from './styles';

export interface ICheckboxFilterOption {
	id: string;
	/** Lower-case fragment used to build the trigger summary, e.g. `notes` renders as "Show notes". */
	name: string;
	label: string;
}

interface ICheckboxFilterDropdownProps {
	options: ICheckboxFilterOption[];
	selectedIds: string[];
	/** Fired once the menu closes, mirroring the committed-on-dismiss behavior of the filter it replaced. */
	onCommit: (selectedIds: string[]) => void;
	ariaLabel: string;
	styles?: StyleDeclarationValue[];
}

const MAX_TRIGGER_LABEL_LENGTH = 25;

export function getCheckboxFilterTriggerLabel(options: ICheckboxFilterOption[], selectedIds: string[]): string {
	if (options.length > 0 && options.every(option => selectedIds.includes(option.id))) {
		return 'Show all';
	}
	const names = options.filter(option => selectedIds.includes(option.id)).map(option => option.name);
	const text = `Show ${names.join(' and ') || 'none'}`;
	return text.length > MAX_TRIGGER_LABEL_LENGTH ? `${text.slice(0, MAX_TRIGGER_LABEL_LENGTH)}...` : text;
}

export function CheckboxFilterDropdown({
	options,
	selectedIds,
	onCommit,
	ariaLabel,
	styles = [],
}: ICheckboxFilterDropdownProps) {
	const [isOpen, setIsOpen] = useState(false);
	const [pendingIds, setPendingIds] = useState<string[]>(selectedIds);

	// While closed the committed props are the truth; the pending set only drives the menu it belongs to.
	const displayedIds = isOpen ? pendingIds : selectedIds;
	const allSelected = options.length > 0 && options.every(option => pendingIds.includes(option.id));

	const handleOpenChange = (open: boolean) => {
		setIsOpen(open);
		if (open) {
			setPendingIds(selectedIds);
			return;
		}
		// An empty selection sends no constraint to the server, which then returns *everything* — the
		// opposite of what an empty filter implies. So an empty selection on dismiss commits as "all".
		onCommit(pendingIds.length > 0 ? pendingIds : options.map(option => option.id));
	};

	const toggleOption = (id: string) => {
		setPendingIds(current => (current.includes(id) ? current.filter(x => x !== id) : [...current, id]));
	};

	const toggleAll = () => {
		setPendingIds(allSelected ? [] : options.map(option => option.id));
	};

	return (
		<Dropdown onOpenChange={handleOpenChange}>
			<DropdownSelectTrigger styles={[styleSheet.trigger, ...styles]} aria-label={ariaLabel}>
				{getCheckboxFilterTriggerLabel(options, displayedIds)}
			</DropdownSelectTrigger>
			<DropdownContent align='end'>
				<DropdownCheckboxItem checked={allSelected} onCheckedChange={toggleAll}>
					Show all
				</DropdownCheckboxItem>
				<DropdownSeparator />
				{options.map(option => (
					<DropdownCheckboxItem
						key={option.id}
						checked={pendingIds.includes(option.id)}
						onCheckedChange={() => toggleOption(option.id)}
					>
						{option.label}
					</DropdownCheckboxItem>
				))}
			</DropdownContent>
		</Dropdown>
	);
}
